import React, { useRef } from 'react';
import debounce from 'debounce';
import { FormattedMessage } from 'react-intl';
import { isAfter } from 'date-fns';

import { useSelector, useDispatch } from 'hooks';
import { QR_CODE_LENGTH } from 'modules/auth/constants';
import {
  hasInvalidCodeError,
  isCodeFieldDisabled,
  getCodeRetryExpiration,
  isCodeReopenVerified,
  getLoginData,
} from 'modules/auth/selectors';
import useQrCodeInput from 'hooks/useQrCodeInput';
import {
  useSendQrCodeMutation,
  useGetMFAConnectionMutation,
  useSendMFACodeMutation,
} from 'modules/auth/service';
import { clearCodeReopenVerified } from 'modules/auth/actions';
import { isCodeLoginEnabled, isMFAonly as isMFAonlySelector } from 'modules/dealers/selectors';
import { getRegistration, getSelfServiceType } from 'modules/selfServices/selectors';
import { getDefaultChannelToSendCode, getRetryExpiration } from 'modules/communication/utils';
import { SelfServiceType } from 'modules/selfServices/types/SelfService';
import { ChannelEnum, MfaScopeEnum } from 'modules/auth/types/LoginRequest';
import { SelfOfficeLoginMethod } from 'modules/dealers/types/ContextResponse';
import useCountdown from 'hooks/useCountdown';
import useQrCodeError from 'hooks/useQrCodeError';
import type PageBaseProps from 'types/PageBase';
import Footer from 'components/ui/PageFooter';
import { Button, PageHeader } from 'components/ui';

import Verify from './Verify';
import QrCodeForm from './qrCodeElements/qrCodeForm';
import QrCodeVideo from './qrCodeElements/qrCodeVideo';
import useQrCodeScanner from 'hooks/useQrCodeScanner';
import { b } from 'utils/i18nUtils';
import { ButtonSize, ButtonType } from 'components/ui/Button';
import { ConnectionTypeLogin } from 'modules/dealers/types/ContextResponse';
import { getCommunicationData } from 'modules/communication/selectors';

const ReopenVerify: React.FC<PageBaseProps> = (props) => {
  const { onNext, onPrev } = props;

  const isMFAonly = useSelector(isMFAonlySelector);
  const codeAvailable = useSelector(isCodeLoginEnabled);
  const hasCodeError = useSelector(hasInvalidCodeError);
  const codeInputDisabled = useSelector(isCodeFieldDisabled);
  const registrationFromSelfService = useSelector(getRegistration) as string;
  const { registration : reg } = useSelector(getLoginData);
  const retryExpiration = useSelector(getCodeRetryExpiration);
  const type = useSelector(getSelfServiceType) as SelfServiceType;
  const communicationData = useSelector(getCommunicationData);

  const registration = reg ?? registrationFromSelfService;
  const { countdown } = useCountdown({ date: retryExpiration });
  const isBreakdownSelfService = type === SelfServiceType.BREAKDOWN;
  const defaultMode = codeAvailable && !isMFAonly && !isBreakdownSelfService ? ConnectionTypeLogin.CODE : ConnectionTypeLogin.MFA;
  const [mode, setMode] = React.useState<ConnectionTypeLogin>(defaultMode);
  const [qrCode, setQrCode] = React.useState('');
  const selfServiceIdRef = useRef('');

  const { handleChange: handleOnChange, codeError } = useQrCodeInput(setQrCode);

  const [sendQrCode, { isLoading: isCodeLoading }] = useSendQrCodeMutation();
  const [getMFAConnection, { isLoading: isMFAConnecting }] = useGetMFAConnectionMutation();
  const [sendMFACode] = useSendMFACodeMutation();

  const dispatch = useDispatch();
  const codeReopenVerified = useSelector(isCodeReopenVerified);

  const isLoading = isCodeLoading || isMFAConnecting;
  const hasProperCode = qrCode.length === QR_CODE_LENGTH;
  const isDisabled = codeInputDisabled && countdown > 0;
  const scannerIsDisabled = isDisabled || !codeAvailable;
  const isSubmitDisabled = !hasProperCode || isDisabled;
  useQrCodeScanner(scannerIsDisabled);

  // Handles QR scan navigation: flag set by auth middleware on sendQrCode.matchFulfilled
  React.useEffect(() => {
    if (codeReopenVerified) {
      console.log('[ReopenVerify] - Reopen code has been verified - Flag is reset - Nav is launched');
      dispatch(clearCodeReopenVerified());
      onNext?.();
    }
  }, [codeReopenVerified, dispatch, onNext]);

  const codeErrorMessage = useQrCodeError({ isDisabled, hasCodeError, codeError, countdown });

  const handleSubmitCode = React.useCallback(async () => {
    try {
      console.log('[ReopenVerify] handleSubmitCode — calling sendQrCode', { type });
      await sendQrCode({ qrCode, type }).unwrap();
      console.log('[ReopenVerify] handleSubmitCode — sendQrCode resolved');
    } catch { /* handle by onQueryStarted */ }
  }, [sendQrCode, qrCode, type]);

  const handleKeyDown = React.useMemo(() => debounce((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !isSubmitDisabled && !isLoading) {
      handleSubmitCode();
    }
  }, 200), [isSubmitDisabled, isLoading, handleSubmitCode]);

  const handleSwitchToMFA = React.useCallback(async () => {
    try {
      // using the unwrap to avoid staling
      const data = await getMFAConnection({
        type,
        value: registration || reg,
        searchType: SelfOfficeLoginMethod.REGISTRATION,
        scope: MfaScopeEnum.REOPEN,
      }).unwrap();

      const channel = type === SelfServiceType.BREAKDOWN ? ChannelEnum.SMS : getDefaultChannelToSendCode(communicationData);
      const retryExp = getRetryExpiration(data, channel);

      if (!retryExp || isAfter(new Date(), new Date(retryExp))) {
        const { connectionId, selfServiceId } = data;
        selfServiceIdRef.current = selfServiceId;
        await sendMFACode({
          channel,
          connectionId,
          selfServiceId,
          customerType: communicationData.customerType,
        });
      }
      // we switch to the other connection mode.
      setMode(ConnectionTypeLogin.MFA);
    } catch { /* handle by onQueryStarted */ }
  }, [getMFAConnection, sendMFACode, type, registration]);

  if (isMFAonly || mode === ConnectionTypeLogin.MFA) {
    return <Verify {...props} selfServiceId={selfServiceIdRef.current} hideGTC />;
  }

  return (
    <>
      <PageHeader hideSteps onPrev={onPrev} title={<FormattedMessage id="login.verificationCode" defaultMessage="Verification code" />}/>
      <div className="main-content">
        <main className="content m-auto!">
          <div className="flex flex-col kioskSharebox:flex-row kioskSharebox:gap-8 kioskSharebox:items-center w-full">
            <div className="flex flex-col kioskSharebox:flex-1">
              <QrCodeForm
                value={qrCode}
                className="mt-4"
                error={codeErrorMessage}
                disabled={isDisabled}
                handleOnChange={handleOnChange}
                handleOnKeyDown={handleKeyDown}
                message={
                  <FormattedMessage
                    id="reopenVerify.qrForm.description"
                    defaultMessage="Please enter the <b>code</b> received by email to reopen the kiosk"
                    values={{ b }}
                    tagName="span"
                  />}
              />
              <div className="flex flex-col mt-6 justify-center text-center">
                <FormattedMessage
                  id="reopenVerify.button.label"
                  defaultMessage="I want to receive an email/SMS to verify my identity."
                  tagName="span"
                />
                <Button
                  size={ButtonSize.SMALL}
                  type={ButtonType.WHITE}
                  disabled={isLoading}
                  onClick={handleSwitchToMFA}
                  className="text-center text-sm text-link underline cursor-pointer p-0! w-fit m-auto"
                >
                  <FormattedMessage
                    id="reopenVerify.button.sendCode"
                    defaultMessage="Send a code"
                    tagName="span"
                  />
                </Button>
              </div>
            </div>
            <QrCodeVideo />
          </div>
        </main>
        <Footer
          onPrev={onPrev}
          onNext={handleSubmitCode}
          disabled={isSubmitDisabled}
          loading={isLoading}
          shouldDisplayBackButton
        />
      </div>
    </>
  );
};

export default ReopenVerify;
